import * as dateFnsCompat from 'utils/date-fns-compat';

import he from 'he';
import fp from 'lodash/fp';
import React from 'react';
import { Breadcrumb, BreadcrumbItem, Button, Col, Grid, Row } from 'react-bootstrap';
import FontAwesome from 'react-fontawesome';
import { connect } from "react-redux";
import { LinkContainer } from 'react-router-bootstrap';
import { push } from 'react-router-redux';
import { branch, compose, lifecycle, renderComponent, withProps } from "recompose";
import { bindActionCreators } from 'redux';
import { contentStatus, dateFormats, routes, searchTileType, userContentCompletionStatus, userEventCompletionStatus } from '../../../../constants';
import { getTaskType, mapRecommendedContent, mapRecommendedCourses } from '../../../../services';
import { businessSelector } from '../../../../state/modules/business';
import { publisherActions } from '../../../../state/modules/publisher';
import { taskActions } from '../../../../state/modules/task';
import { taskSelector } from '../../../../state/modules/task/selectors';
import { noCapMap } from '../../../../utils';
import { Icon } from '../../../components';
import Loading from '../../loading';
import ContentTile from '../partials/content-tile';
import CourseTile from '../partials/course-tile';

const TileSlides = ({ contents, task }) => noCapMap((chunk, chunkIndex) =>
  <Row key={chunkIndex} className='card-gutter row-slider bottom-gap'>
    {
      noCapMap((tile, index) =>
        <Col key={index} xs={12} md={3}>
          {
            tile.type === searchTileType.Content ?
              <ContentTile
                {...tile}
                taskId={task.id}
              /> : null
          }
          {
            tile.type === searchTileType.Course ?
              <CourseTile
                {...tile}
                taskId={task.id}
              /> : null
          }
        </Col>
      )(chunk)
    }
  </Row>
)(fp.flow(
  fp.chunk(4)
)(contents))

const TaskView = ({
  goTo,
  task,
  taskType,
  children,
  completedTiles,
  inProgressTiles,
  toBeCompletedTiles,
  failedTiles
}) => (<div id="task-view" className='base-layout'>
  <Grid className='layout--header'>
    <Row className='button-gutter'>
      <Col xs={12} md={5}>
        <Breadcrumb>
          <LinkContainer to={routes.app.tasks.home}>
            <BreadcrumbItem>
              Tasks
            </BreadcrumbItem>
          </LinkContainer>
          <BreadcrumbItem active>
            {he.decode(task.name)}
          </BreadcrumbItem>
        </Breadcrumb>
      </Col>
    </Row>
    <Row className='no-gutter task-heading'>
      <Col xs={12} md={7} className='no-padding'>
        <h1><Icon icon='training-large' />{he.decode(task.name)}</h1>
      </Col>
      <Col xs={12} md={5} className='no-padding'>
        <h4><Icon icon='user' />{`Set by ${he.decode(task.assigner)}`}</h4>
        <h4><FontAwesome name='calendar-o' className='icon' />{`Due by ${dateFnsCompat.format(task.dueDate, dateFormats.ShortMonthLongDate)}`}</h4>
      </Col>
    </Row>
    <Row className='no-gutter'>
      <Col xs={12} className='no-padding'>
        <p>{he.decode(task.description)}</p>
      </Col>
    </Row>
  </Grid>
  <Grid className='layout--section'>
    <Row className='no-gutter'>
      <Col xs={12}>
        <h2>Task content</h2>
      </Col>
    </Row>
    {
      inProgressTiles.length > 0 ?
        <div>
          <hr />
          <Row className='no-gutter'>
            <Col xs={12}>
              <h3>In progress</h3>
            </Col>
          </Row>
          <TileSlides contents={inProgressTiles} task={task} />
        </div> : null
    }
    {
      toBeCompletedTiles.length > 0 ?
        <div>
          <hr />
          <Row className='no-gutter'>
            <Col xs={12}>
              <h3>To be completed</h3>
            </Col>
          </Row>
          <TileSlides contents={toBeCompletedTiles} task={task} />
        </div> : null
    }
    {
      completedTiles.length > 0 ?
        <div>
          <hr />
          <Row className='no-gutter'>
            <Col xs={12}>
              <h3>Completed content</h3>
            </Col>
          </Row>
          <TileSlides contents={completedTiles} task={task} />
        </div> : null
    }
    {
      failedTiles.length > 0 ?
        <div>
          <hr />
          <Row className='no-gutter'>
            <Col xs={12}>
              <h3>Incomplete content</h3>
            </Col>
          </Row>
          <TileSlides contents={failedTiles} task={task} />
        </div> : null
    }
    <Row key='back-button' className='card-gutter row-slider bottom-gap'>
      <Col className='flex-center'>
        <LinkContainer to={routes.app.tasks.home}>
          <Button bsStyle='plain'>Back</Button>
        </LinkContainer>
      </Col>
    </Row>
  </Grid>
</div>);

const mapStateToProps = (state) => ({
  isFetchingTask: state.task.isFetching,
  task: taskSelector(state),
  business: businessSelector(state),
  publishers: state.publisher.publishers,
  isFetchingPublishers: state.publisher.isFetching,
  mapContent: mapRecommendedContent(state.industry.cpdCategorys, state.publisher.publishers, businessSelector(state)),
  mapCourses: mapRecommendedCourses(state.industry.cpdCategorys, state.publisher.publishers)
});

const mapDispatchToProps = (dispatch) => ({
  goTo: bindActionCreators(push, dispatch),
  getTask: bindActionCreators(taskActions.task.get, dispatch),
  getPublishers: bindActionCreators(publisherActions.publishers.get, dispatch)
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withProps(({
    match: {
      params: {
        taskId
      }
    }
  }) => ({
    taskId
  })),
  lifecycle({
    componentWillMount() {
      const {
        taskId,
        getTask,
        getPublishers,
        publishers,
        isFetchingPublishers
      } = this.props;
      getTask(taskId);

      if (fp.isEmpty(publishers) && !isFetchingPublishers) {
        getPublishers();
      }
    }
  }),
  branch(
    ({ isFetchingTask, task, taskId, publishers }) => (isFetchingTask) || (!task) || task.id !== parseInt(taskId, 10) || fp.isEmpty(publishers),
    renderComponent(Loading)
  ),
  withProps(
    ({
      task,
      mapContent,
      mapCourses
    }) => ({
      tiles: mapContent(task.contents).concat(mapCourses(task.courses))
    })
  ),
  withProps(
    ({ goTo, task, mapContent, tiles }) => ({
      taskType: getTaskType(task),
      children: () => <Row className='no-gutter'>
        <Col xs={12} className='no-padding'>
          <Button bsStyle="plain" className='full' onClick={() => goTo(routes.app.tasks.home)}>Back</Button>
        </Col>
      </Row>,
      completedTiles: fp.filter(
        ({ type, completionStatus }) => (
          (type === searchTileType.Course) && (completionStatus === userEventCompletionStatus.Completed)
        ) || (
            (type === searchTileType.Content) && (completionStatus === userContentCompletionStatus.Completed)
          )
      )(tiles),
      inProgressTiles: fp.filter(
        ({ type, completionStatus, status }) => (
          (type === searchTileType.Course) && (completionStatus === userEventCompletionStatus.Registered)
        ) || (
            (type === searchTileType.Content) && (completionStatus === userContentCompletionStatus.InProgress) && (status === contentStatus.Published.value)
          )
      )(tiles),
      toBeCompletedTiles: fp.filter(
        ({ type, completionStatus, status }) => status === contentStatus.Published.value && ((
          (type === searchTileType.Course) && (completionStatus === userEventCompletionStatus.Unknown)
        ) || (
            (type === searchTileType.Content) && (completionStatus === userContentCompletionStatus.Unknown)
          ))
      )(tiles),
      failedTiles: fp.filter(
        ({ type, completionStatus, status }) => (
          (type === searchTileType.Course) && (completionStatus === userEventCompletionStatus.Failed || (status !== contentStatus.Published.value && completionStatus === userEventCompletionStatus.Unknown))
        ) || (
            (type === searchTileType.Content) && (completionStatus === userContentCompletionStatus.Failed || (status !== contentStatus.Published.value && completionStatus !== userContentCompletionStatus.Completed))
          )
      )(tiles)
    })
  )
)(TaskView)


// WEBPACK FOOTER //
// ./src/containers/pages/app/tasks/task-view.js